import { safeSessionStorageSetItem } from "./browserStorage";

const SENSITIVE_KEY_PATTERN =
  /(authorization|password|passwd|secret|token|access|refresh|jwt|credential|cookie)/i;
const MAX_SANITIZE_DEPTH = 5;
const MAX_STRING_LENGTH = 2000;

const sanitizeString = (value) => {
  const normalized = String(value);
  const redacted = normalized
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
    .replace(/(token|access|refresh|jwt)=([^&\s]+)/gi, "$1=[redacted]");

  if (redacted.length <= MAX_STRING_LENGTH) return redacted;
  return `${redacted.slice(0, MAX_STRING_LENGTH)}...`;
};

export const sanitizeErrorData = (value, depth = 0) => {
  if (value === null || value === undefined) return value;

  if (typeof value === "string") return sanitizeString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (depth >= MAX_SANITIZE_DEPTH) return "[max-depth]";

  if (Array.isArray(value)) {
    return value.slice(0, 25).map((item) => sanitizeErrorData(item, depth + 1));
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((next, [key, item]) => {
      next[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? "[redacted]"
        : sanitizeErrorData(item, depth + 1);
      return next;
    }, {});
  }

  return sanitizeString(value);
};

const getRouteContext = () => {
  if (typeof window === "undefined") {
    return { pathname: "", search: "" };
  }

  return {
    pathname: window.location?.pathname || "",
    search: window.location?.search || "",
  };
};

const getUserAgent = () => {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || "";
};

const serializeThrowable = (error) => {
  if (!error) {
    return {
      message: "",
      stack: "",
      response: undefined,
    };
  }

  if (typeof error === "string") {
    return {
      message: sanitizeString(error),
      stack: "",
      response: undefined,
    };
  }

  const response = error?.response
    ? {
        data: sanitizeErrorData(error.response.data),
        status: error.response.status,
      }
    : undefined;

  return {
    message: sanitizeString(error.message || String(error)),
    stack: error.stack ? sanitizeString(error.stack) : "",
    response,
  };
};

const buildBasePayload = (type) => ({
  type,
  ...getRouteContext(),
  timestamp: new Date().toISOString(),
  userAgent: getUserAgent(),
});

const persistPayload = (key, payload) => {
  try {
    safeSessionStorageSetItem(key, JSON.stringify(payload));
  } catch {
    safeSessionStorageSetItem(
      key,
      JSON.stringify({
        type: payload?.type || "unknown",
        message: "No se pudo serializar el error completo.",
        timestamp: new Date().toISOString(),
      })
    );
  }
};

export const reportReactFatalError = (error, errorInfo = {}) => {
  const serialized = serializeThrowable(error);
  const payload = {
    ...buildBasePayload("react-render-error"),
    message: serialized.message,
    stack: serialized.stack,
    componentStack: errorInfo?.componentStack || "",
    response: serialized.response,
  };

  console.error("REACT_FATAL_RENDER_ERROR", payload);
  persistPayload("last-react-fatal-error", payload);
  return payload;
};

export const reportWindowErrorEvent = (event) => {
  const serialized = serializeThrowable(event?.error);
  const payload = {
    ...buildBasePayload("window-error"),
    message: sanitizeString(event?.message || serialized.message || "Error no atrapado."),
    stack: serialized.stack,
    filename: event?.filename || "",
    lineno: event?.lineno || null,
    colno: event?.colno || null,
    response: serialized.response,
  };

  console.error("GLOBAL_WINDOW_ERROR", payload);
  persistPayload("last-global-window-error", payload);
};

export const reportUnhandledRejectionEvent = (event) => {
  const serialized = serializeThrowable(event?.reason);
  const payload = {
    ...buildBasePayload("unhandledrejection"),
    message: serialized.message || "Promesa rechazada sin manejar.",
    stack: serialized.stack,
    response: serialized.response,
  };

  console.error("GLOBAL_UNHANDLED_REJECTION", payload);
  persistPayload("last-global-unhandled-rejection", payload);
};

export const registerGlobalErrorListeners = () => {
  if (typeof window === "undefined") return undefined;

  const onError = (event) => reportWindowErrorEvent(event);
  const onUnhandledRejection = (event) => reportUnhandledRejectionEvent(event);

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
};
